import { useState } from "react";
import { Button, Checkbox, Modal, TextArea } from "@core/components";
import { useRejectApproval } from "@modules/docs/annotations/hooks";

const REJECTION_REASONS = [
  "Non-conformity with standards",
  "Incorrect or incomplete information",
  "Technical errors",
  "Missing required signatures or stamps",
  "Revision required per review comments",
  "Other",
] as const;

const OTHER_REASON = "Other";

type RejectApprovalProps = {
  onClose: VoidFunction;
};

export function RejectApproval({ onClose }: RejectApprovalProps) {
  const rejectApprovalMutation = useRejectApproval();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [otherText, setOtherText] = useState("");

  const toggleReason = (reason: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(reason)) {
        next.delete(reason);
      } else {
        next.add(reason);
      }
      return next;
    });
  };

  const isOtherSelected = selected.has(OTHER_REASON);

  const canSubmit =
    selected.size > 0 &&
    (!isOtherSelected || otherText.trim().length > 0) &&
    !rejectApprovalMutation.isPending;

  const handleReject = () => {
    const cleanedReasons = [
      ...[...selected].filter((r) => r !== OTHER_REASON),
      ...(isOtherSelected && otherText.trim() ? [otherText.trim()] : []),
    ];
    rejectApprovalMutation.mutate(cleanedReasons);
  };

  return (
    <Modal title="Reject Approval" size="md" onClose={onClose}>
      <div className="flex flex-col gap-4 px-4 py-4">
        <p className="text-sm text-neutral-600">
          Select the reasons for rejection:
        </p>

        <div className="flex flex-col gap-3">
          {REJECTION_REASONS.map((reason) => (
            <div
              key={reason}
              className="flex items-center gap-3 cursor-pointer select-none"
              onClick={() => toggleReason(reason)}
            >
              <Checkbox checked={selected.has(reason)} />
              <span className="text-sm text-neutral-800">{reason}</span>
            </div>
          ))}
        </div>

        {isOtherSelected && (
          <TextArea
            placeholder="Describe the reason..."
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            rows={3}
          />
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={rejectApprovalMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-accent-red-700 border-accent-red-800"
            size="sm"
            disabled={!canSubmit}
            isLoading={rejectApprovalMutation.isPending}
            onClick={handleReject}
          >
            Reject
          </Button>
        </div>
      </div>
    </Modal>
  );
}
